import { usePlayheadStore } from '../store/playheadStore';
import { useEditorStore } from '../store/editorStore';
import { useLoopStore } from '../store/loopStore';
import { LOOK_AHEAD_MS } from '../constants';
import type { RendererContext } from './createRenderer';
import type { VideoPlane } from './videoPlane';
import type { FrameDecoder } from '../engine/decoder';
import type { IAudioEngine } from '../engine/audioEngineInterface';
import type { Clip } from '../types';

/**
 * Find the clip under the playhead for a given track type.
 */
function findClipAt(timelineMs: number, type: 'video' | 'audio'): Clip | null {
  const tracks = useEditorStore.getState().project.tracks;
  for (const track of tracks) {
    if (track.type !== type) continue;
    for (const clip of track.clips) {
      if (timelineMs >= clip.startMs && timelineMs < clip.startMs + clip.durationMs) {
        return clip;
      }
    }
  }
  return null;
}

/**
 * Map playhead (timeline) time to source media time using a clip's sourceOffsetMs.
 */
function clipToSourceTime(timelineMs: number, clip: Clip): number {
  return clip.sourceOffsetMs + (timelineMs - clip.startMs);
}

/**
 * Map source time back to timeline time for a clip (inverse of clipToSourceTime).
 */
function sourceToTimelineTime(sourceMs: number, clip: Clip): number {
  return clip.startMs + (sourceMs - clip.sourceOffsetMs);
}

export interface RenderLoop {
  start: () => void;
  stop: () => void;
  setDecoder: (decoder: FrameDecoder | null) => void;
  setAudioEngine: (engine: IAudioEngine | null) => void;
}

export function createRenderLoop(
  ctx: RendererContext,
  videoPlane: VideoPlane,
): RenderLoop {
  let rafId = 0;
  let running = false;
  let lastRenderedTimeMs = -1;
  let decoder: FrameDecoder | null = null;
  let audioEngine: IAudioEngine | null = null;
  let seeking = false;

  // Track which audio clip we're currently playing so we can detect transitions
  let currentAudioClip: Clip | null = null;

  let lastTickTime = 0;
  let decodeLogCount = 0;

  async function decodeUpdateRender(timeMs: number) {
    if (!decoder) {
      if (decodeLogCount++ % 300 === 0) console.log('[RenderLoop] no decoder');
      ctx.renderer.render(ctx.scene, ctx.camera);
      return;
    }

    if (seeking) {
      if (decodeLogCount++ % 300 === 0) console.log('[RenderLoop] seeking, skip');
      ctx.renderer.render(ctx.scene, ctx.camera);
      return;
    }

    if (Math.abs(timeMs - lastRenderedTimeMs) < 1) {
      ctx.renderer.render(ctx.scene, ctx.camera);
      return;
    }

    seeking = true;
    try {
      const frame = await decoder.getFrame(timeMs);
      if (frame) {
        videoPlane.updateFrame(frame);
        lastRenderedTimeMs = timeMs;
      }
    } catch {
      // Frame decode failed — render what we have
    } finally {
      seeking = false;
    }

    ctx.renderer.render(ctx.scene, ctx.camera);

    // Pre-fetch ahead of the playhead (fire-and-forget, non-blocking)
    if (decoder) {
      decoder.prefetch(timeMs, timeMs + LOOK_AHEAD_MS);
    }
  }

  function tick(timestamp: number) {
    if (!running) return;

    const elapsed = lastTickTime > 0 ? timestamp - lastTickTime : 0;
    lastTickTime = timestamp;

    const playhead = usePlayheadStore.getState();

    if (playhead.playing && audioEngine) {
      // Find the audio clip under current playhead
      const currentMs = playhead.currentTimeMs;
      const audioClip = findClipAt(currentMs, 'audio');

      if (audioClip && audioEngine.playing) {
        // Audio is playing — derive timeline time from audio clock
        const audioSourceMs = audioEngine.getCurrentTimeMs();
        const timelineMs = sourceToTimelineTime(audioSourceMs, audioClip);

        // Check if we've gone past this clip's end
        if (timelineMs >= audioClip.startMs + audioClip.durationMs) {
          audioEngine.stop();
          currentAudioClip = null;
          usePlayheadStore.getState().setCurrentTime(audioClip.startMs + audioClip.durationMs);
        } else {
          usePlayheadStore.getState().setCurrentTime(timelineMs);
        }
      } else if (audioClip && !audioEngine.playing) {
        // We entered an audio clip — start audio from the correct source offset
        const sourceMs = clipToSourceTime(currentMs, audioClip);
        audioEngine.play(sourceMs, audioClip.assetId);
        currentAudioClip = audioClip;
      } else if (!audioClip && audioEngine.playing) {
        // We left an audio clip — stop audio
        audioEngine.stop();
        currentAudioClip = null;
      }

      // If no audio clip and no audio playing, advance playhead by actual elapsed time
      if (!audioClip && !audioEngine.playing) {
        const dt = Math.min(elapsed, 100); // cap at 100ms to avoid jumps
        if (dt > 0) {
          usePlayheadStore.getState().advance(dt);
        }
      }

      // Selection region: constrain playback to selection boundaries
      const loop = useLoopStore.getState();
      const nowMsLoop = usePlayheadStore.getState().currentTimeMs;
      if (loop.selectionStart !== null && loop.selectionEnd !== null &&
          nowMsLoop >= loop.selectionEnd) {
        if (loop.loopEnabled) {
          // Loop back to selectionStart
          usePlayheadStore.getState().setCurrentTime(loop.selectionStart);
          audioEngine.stop();
          currentAudioClip = null;
          const loopAudioClip = findClipAt(loop.selectionStart, 'audio');
          if (loopAudioClip) {
            const loopSourceMs = clipToSourceTime(loop.selectionStart, loopAudioClip);
            audioEngine.play(loopSourceMs, loopAudioClip.assetId);
            currentAudioClip = loopAudioClip;
          }
        } else {
          // Stop at selectionEnd
          usePlayheadStore.getState().setPlaying(false);
          usePlayheadStore.getState().setCurrentTime(loop.selectionEnd);
          audioEngine.stop();
          currentAudioClip = null;
        }
      }

      // Clamp to project duration
      const duration = useEditorStore.getState().project.durationMs;
      const nowMs = usePlayheadStore.getState().currentTimeMs;
      if (duration > 0 && nowMs >= duration) {
        usePlayheadStore.getState().setPlaying(false);
        usePlayheadStore.getState().setCurrentTime(duration);
        audioEngine.stop();
        currentAudioClip = null;
      }
    }

    // Update effect clip activation states
    if (playhead.playing && audioEngine) {
      const tracks = useEditorStore.getState().project.tracks;
      const effectCurrentMs = usePlayheadStore.getState().currentTimeMs;
      for (const track of tracks) {
        if (track.type === 'effect' && track.effectClips?.length && track.parentTrackId && 'updateEffectClipStates' in audioEngine) {
          (audioEngine as { updateEffectClipStates: (ms: number, clips: typeof track.effectClips, parentTrackId?: string) => void })
            .updateEffectClipStates(effectCurrentMs, track.effectClips, track.parentTrackId);
        }
      }
    }

    // Resolve video source time and render
    const currentMs = usePlayheadStore.getState().currentTimeMs;
    const videoClip = findClipAt(currentMs, 'video');
    const sourceMs = videoClip ? clipToSourceTime(currentMs, videoClip) : currentMs;
    decodeUpdateRender(sourceMs);

    ctx.renderer.render(ctx.scene, ctx.camera);

    rafId = requestAnimationFrame(tick);
  }

  return {
    start() {
      if (running) return;
      running = true;
      lastTickTime = 0;
      rafId = requestAnimationFrame(tick);
    },
    stop() {
      running = false;
      cancelAnimationFrame(rafId);
    },
    setDecoder(d) {
      decoder = d;
      lastRenderedTimeMs = -1;
    },
    setAudioEngine(engine) {
      audioEngine = engine;
      currentAudioClip = null;
    },
  };
}
